/**
 * What a plan wall offers — the plan page, or a mailto to support.
 *
 * A SuperAdmin chooses it (`paywall_cta_mode` / `paywall_support_email`,
 * platform settings on the API) and the desk reads it from
 * `GET /platform/paywall-cta`. Only the screens that opt in read it — the
 * notice analysis panel, the document-type suggestion card and the upload
 * page's analysis follow-up; every other wall is `PaywallNotice` as it was.
 *
 * Pure, so it is tested without React. Anything unreadable is `plans`: a
 * wall that cannot say what to offer offers what it always did.
 */
import type { PaywallGate } from './paywallCopy';

export type PaywallCtaMode = 'plans' | 'support_email';

export interface PaywallCta {
  mode: PaywallCtaMode;
  support_email: string;
}

export const DEFAULT_SUPPORT_EMAIL = 'support@globalsynapsetech.com';

export const DEFAULT_PAYWALL_CTA: PaywallCta = { mode: 'plans', support_email: DEFAULT_SUPPORT_EMAIL };

const EMAIL_RE = /^[^@\s<>"']+@[^@\s<>"']+\.[^@\s<>"']+$/;

export function isEmailAddress(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 254 && EMAIL_RE.test(value);
}

/** The server's answer, or the default for any part of it that is not usable.
 *  `support_email` mode with no usable address falls back to `plans`. */
export function normalisePaywallCta(raw: unknown): PaywallCta {
  if (!raw || typeof raw !== 'object') return DEFAULT_PAYWALL_CTA;
  const r = raw as Record<string, unknown>;
  const email = typeof r.support_email === 'string' ? r.support_email.trim() : '';
  if (r.mode === 'support_email' && isEmailAddress(email)) {
    return { mode: 'support_email', support_email: email };
  }
  return { mode: 'plans', support_email: isEmailAddress(email) ? email : DEFAULT_SUPPORT_EMAIL };
}

/** "notice_analysis" → "notice analysis". */
export function humaniseFeatureKey(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** What the subject names: the screen's own label, else the gate's feature
 *  key, else a neutral phrase. */
export function planWallFeatureName(
  gate: Pick<PaywallGate, 'requiredFeature'> | null | undefined,
  featureLabel?: string | null,
): string {
  const label = featureLabel?.trim();
  if (label) return label;
  const key = gate?.requiredFeature?.trim();
  if (key) return humaniseFeatureKey(key);
  return 'a feature not in my plan';
}

/** `mailto:` with the subject "LexVio: access to <feature>". */
export function supportMailto(email: string, featureName: string): string {
  const subject = `LexVio: access to ${featureName}`;
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`;
}
